import { useEffect, useState } from 'react'
import React from 'react'
import DetailsWrapper from '../../../layouts/DetailsWrapper'
import { TextField, MenuItem, Typography } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { useParams, useNavigate } from 'react-router-dom'
import Feedback from '../../../components/Feedback'
import LogoLoader from '../../../components/LogoLoader'
import PermMediaIcon from '@mui/icons-material/PermMedia'
import ContractDetails from './components/ContractDetails'
import EditIcon from '@mui/icons-material/Edit'
import { bffService } from '../../../services/utils/axios'
import { useAuth } from '../../../services/contexts'
import ErrorFeedback from '../../../components/DataTable/Feedback'
import {
  PaperItem,
  PaperItemBody,
  StyledTypographyHeading,
  CustomizedButton,
} from './styles'
import RouteNotFound from '../../RouteNotFound'
import EditContractFormPopup from './EditContractFormPopup'

function displayNameFromContract(contract, fallback) {
  if (!contract) return fallback
  if (contract.salutation === 'COMPANY' && contract.companyName) {
    return contract.companyName
  }
  if (contract.fullName) return contract.fullName
  const first = contract.firstName?.trim() ?? ''
  const last = contract.lastName?.trim() ?? ''
  const joined = [first, last].filter(Boolean).join(' ')
  return joined || contract.email || fallback
}

/**
 * The main component that shows the user details after user clicks on the details in the table of Nutzerverwaltung page.
 * This also holds the button to edit the details of a contract.
 *
 * @returns {React.ReactNode} - Returns all the component that are show in user details page.
 */

export default function ContractDetail() {
  const { token } = useAuth()
  const { t } = useTranslation()
  const params = useParams()
  const navigate = useNavigate()
  const [loading, setLoading] = useState(false)
  const [selectedContractId, setSelectedContractId] = useState(null)
  const [isFormPopupOpen, setIsFormPopupOpen] = useState(false)
  const [completed, setCompleted] = useState(false)
  const [error, setError] = useState(false)
  const [showEditButton, setShowEditButton] = useState(false)
  const [editContractData, setEditContractData] = useState()
  const [currentContract, setCurrentContract] = useState(null)
  const [userEmail, setUserEmail] = useState(null)
  const [user, setUser] = useState(null)

  const [contractDataDto, setContractDataDto] = useState([])
  // Load data based on URL parameters - always fetch from API
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true)
        const { email, user, contract: contractUuid } = params
        const userEmail = email || user // Handle both route patterns

        if (userEmail) {
          setUserEmail(userEmail)

          // Always fetch contracts by email from API
          const contractsResponse = await bffService.get('/contracts', {
            params: { email: userEmail },
          })
          if (contractsResponse.data && contractsResponse.data.data) {
            const contracts = contractsResponse.data.data
            setContractDataDto(contracts)

            // Create a user object from the email and contracts
            setUser({
              email: userEmail,
              fullName: displayNameFromContract(contracts[0], userEmail),
              contractDataDto: contracts,
            })

            // If we have a specific contract UUID in the URL, select it and fetch its details
            if (contractUuid) {
              setSelectedContractId(contractUuid)

              // Check if the contract is in the list, if not fetch it separately
              const contractInList = contracts.find(
                (c) => c.uuid === contractUuid
              )

              if (contractInList) {
                // Contract is in the list, use it directly
                setEditContractData(contractInList)
                setCurrentContract(contractInList)
                setShowEditButton(true)
              } else {
                // Contract not in list, fetch it separately
                try {
                  const contractResponse = await bffService.get(
                    `/contracts/${contractUuid}`
                  )
                  if (contractResponse.data && contractResponse.data.data) {
                    const contractData = contractResponse.data.data
                    setEditContractData(contractData)
                    setCurrentContract(contractData)
                    setShowEditButton(true)

                    // Add this contract to the list
                    setContractDataDto((prev) => [...prev, contractData])
                    setUser((prev) => ({
                      ...prev,
                      fullName: displayNameFromContract(
                        contractData,
                        prev.email ?? userEmail
                      ),
                      contractDataDto: [...prev.contractDataDto, contractData],
                    }))
                  }
                } catch (contractError) {
                  // Don't set error for specific contract fetch failure
                  // User can still interact with other contracts in the dropdown
                }
              }
            }
          } else {
            // Don't set error, just create empty user
            setContractDataDto([])
            setUser({
              email: userEmail,
              fullName: userEmail,
              contractDataDto: [],
            })
          }
        } else if (contractUuid) {
          // Handle case where we only have contract UUID (legacy support)
          try {
            const contractResponse = await bffService.get(
              `/contracts/${contractUuid}`
            )
            if (contractResponse.data && contractResponse.data.data) {
              const contractData = contractResponse.data.data
              setEditContractData(contractData)
              setCurrentContract(contractData)
              setShowEditButton(true)
              setSelectedContractId(contractUuid)

              // If contract has email, fetch other contracts for this email
              if (contractData.email) {
                setUserEmail(contractData.email)

                try {
                  const contractsResponse = await bffService.get(
                    '/contracts',
                    {
                      params: { email: contractData.email },
                    }
                  )
                  if (contractsResponse.data && contractsResponse.data.data) {
                    const contracts = contractsResponse.data.data
                    setContractDataDto(contracts)
                    setUser({
                      email: contractData.email,
                      fullName: displayNameFromContract(
                        contracts[0],
                        contractData.email
                      ),
                      contractDataDto: contracts,
                    })
                  } else {
                    // Fallback to just this contract
                    setContractDataDto([contractData])
                    setUser({
                      email: contractData.email,
                      fullName: displayNameFromContract(
                        contractData,
                        contractData.email
                      ),
                      contractDataDto: [contractData],
                    })
                  }
                } catch (error) {
                  setContractDataDto([contractData])
                  setUser({
                    email: contractData.email,
                    fullName: displayNameFromContract(
                      contractData,
                      contractData.email
                    ),
                    contractDataDto: [contractData],
                  })
                }
              }
            } else {
              // Don't set error, just continue
            }
          } catch (contractError) {
            console.error('Error fetching contract:', contractError)
            setError(true)
          }
        }
      } catch (error) {
        console.error('Error loading data:', error)
        // Only set error for critical failures (network errors, auth errors, etc.)
        if (error.response?.status >= 500 || !error.response) {
          setError(true)
        }
      } finally {
        setLoading(false)
      }
    }

    if (token && (params.email || params.user || params.contract)) {
      loadData()
    }
  }, [token, params])

  const handleContractChange = (event) => {
    const selectedContractId = event.target.value
    const selectedContract = contractDataDto.find(
      (contract) => contract.uuid === selectedContractId
    )

    if (selectedContract) {
      setEditContractData(selectedContract)
      setCurrentContract(selectedContract)
      setShowEditButton(true)
      setSelectedContractId(selectedContractId)

      // Navigate to the new URL with the selected contract
      const email = userEmail || user?.email || params.email
      if (email) {
        navigate(`/nutzer/${email}/contracts/${selectedContractId}`)
      }
    }
  }
  /**
   * This api call is use to fetch the single contract data.
   * For detail response visit the following page in docs.
   * [API Respnse Documentation](./src/docs/apiResponse.md)
   *
   */
  const fetchContractDetailsById = async () => {
    if (!editContractData?.uuid) return

    try {
      const response = await bffService.get(
        `/contracts/${editContractData.uuid}`
      )
      if (response.data) {
        const updatedContract = response.data

        // Update all relevant states with the fresh data
        setEditContractData(updatedContract)
        setCurrentContract(updatedContract)

        // Update the contract in the dropdown list
        setContractDataDto((prevContracts) =>
          prevContracts.map((contract) =>
            contract.uuid === updatedContract.uuid ? updatedContract : contract
          )
        )

        // Update the user's contract data
        setUser((prevUser) => ({
          ...prevUser,
          contractDataDto: prevUser.contractDataDto.map((contract) =>
            contract.uuid === updatedContract.uuid ? updatedContract : contract
          ),
        }))
      }
    } catch (error) {
      console.error('Error refreshing contract:', error)
      if (error.response?.status >= 500 || !error.response) {
        setError(true)
      }
    }
  }

  const handleClick = (value) => {
    setEditContractData(value)
    setIsFormPopupOpen(true)
  }

  const closeFeedback = () => {
    setCompleted(false)
    // setPayload(null)
    setError(false)
  }
  // Show loading if we're fetching data and don't have user data yet
  if (loading && !user) {
    return <LogoLoader loading={true} />
  }

  // Show not found only if we're not loading and don't have valid URL params or user data
  if (!loading && !user && !params.email && !params.contract) {
    return (
      <RouteNotFound
        title="Seite nicht gefunden"
        description="Bitte überprüfen Sie den Link oder gehen Sie zur Benutzerverwaltung"
        buttonText="zum Benutzerverwaltung"
        to="/nutzer"
      />
    )
  }
  const headerDisplayName = displayNameFromContract(
    contractDataDto[0],
    user?.fullName ?? userEmail ?? '--'
  )
  return (
    <>
      <DetailsWrapper
        title={t('LBLUserDetails')}
        loading={loading}
        helmet="invoices"
        backButtonConfig={{ label: t('lblBacktoview'), url: '/nutzer' }}
        contract={''}
        fullName={headerDisplayName}
      >
        <PaperItem>
          <StyledTypographyHeading>{t('LBLContracts')}</StyledTypographyHeading>

          <div
            style={{
              width: '20%',
              height: '70px',
              textAlign: 'left',
              float: 'left',
            }}
          >
            <div
              style={{
                backgroundColor: 'transparent',
                marginLeft: '25px',
                marginTop: '-15px',
              }}
            >
              <TextField
                select
                id="contractDetails"
                label={t('LBLContractSelection')}
                onChange={handleContractChange}
                value={selectedContractId || ''}
                name="contractDetailSelection"
                style={{
                  width: '300px',
                  color: (theme) => theme.props.surfaceBright,
                }}
                SelectProps={{
                  MenuProps: {
                    PaperProps: {
                      style: {
                        width: '200px',
                        color: (theme) => theme.props.surfaceBright,
                      },
                    },
                  },
                }}
              >
                {contractDataDto && contractDataDto.length > 0 ? (
                  contractDataDto.map((contract, index) => (
                    <MenuItem size="large" key={index} value={contract.uuid}>
                      {contract?.energyRole === 'CONSUMER'
                        ? ` ${t('LBLConsumerContract')} ${index + 1}`
                        : `${t('LBLProducerContract')} ${index + 1}`}
                    </MenuItem>
                  ))
                ) : (
                  <MenuItem value="">
                    <Typography fontWeight="bold" variant="h4" gutterBottom>
                      {t('LBLSelectContract')}
                    </Typography>
                  </MenuItem>
                )}
              </TextField>
            </div>
          </div>
          {showEditButton && (
            <CustomizedButton
              id="Edit-button"
              onClick={() => handleClick(editContractData)}
              variant="contained"
            >
              <EditIcon />
              {t('LBLEditUserDetail')}
            </CustomizedButton>
          )}
        </PaperItem>

        {currentContract ? (
          <ContractDetails
            key={currentContract.uuid}
            contract={currentContract}
          />
        ) : (
          <>
            <PaperItemBody>
              <PermMediaIcon
                sx={{
                  fontSize: 140,
                  padding: '20px',
                  color: (theme) => theme.tokens.secondary,
                }}
              />
              <Typography fontWeight="bold" variant="h2" gutterBottom>
                {t('LBLSelectContract')}
              </Typography>
            </PaperItemBody>
          </>
        )}
      </DetailsWrapper>

      <EditContractFormPopup
        onLoadingChanged={setLoading}
        onFormCompleted={(completed) => {
          setCompleted(completed)
          if (completed) {
            fetchContractDetailsById()
          }
        }}
        onIsError={setError}
        contract={editContractData}
        isEditingContract={isFormPopupOpen}
        refetch={fetchContractDetailsById}
        onIsFormOpened={setIsFormPopupOpen}
      />

      <Feedback
        open={completed}
        title={t('LBLContractUpdated')}
        message={
          <>
            <Typography align="center">
              {t('LBLUserContractUpdated')}
            </Typography>
          </>
        }
        handleClose={closeFeedback}
      />
      <ErrorFeedback
        open={error}
        title={t('LBLErrorTitle')}
        severity="warning"
        message={
          <Typography variant="subtitle1" sx={{ width: 400, mx: 10 }}>
            {t('LBLContractUpdateEror')}
          </Typography>
        }
        handleClose={closeFeedback}
      />
    </>
  )
}
